import React, { createContext, Dispatch, JSX, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';
import { logger } from '../util/Logger';
import { QuickShareUrlHash } from 'api/QuickShareUrlHash';
import { AuthenticatedQuickShareDetails, QuickShareComment, QuickShareContent, QuickShareFileState } from 'types';
import { JsonWebToken } from 'api/JsonWebToken';
import { SymmetricKey } from 'util/Cryptography/SymmetricKey';
import { PublicKey } from 'api/PublicKey';
import base64url from 'base64url';
import { PrivateKey } from 'api/PrivateKey';
import { QuickShareApiJwtGenerator } from 'util/Serialization/ApiStoreAuthorization';

interface Props {
  urlHash?: QuickShareUrlHash;
  loading?: boolean;
  quickShareDetails?: AuthenticatedQuickShareDetails;
  quickShareContent?: QuickShareContent;
  submittedTotp?: string;
  totpSubmitting?: boolean;
  isLoggingIn?: boolean;
  isNotFound?: boolean;
  refreshJwt?: JsonWebToken;
  quickSharePrivateKey?: PrivateKey;
  setLoggingIn?: boolean;
  loginError?: string;
  realmLogo?: string;
  quickShareFilesStates?: QuickShareFileState[];
  quickShareFilesComments?: QuickShareComment[];
  children: ReactNode;
}

type QuickShareContextType = {
  urlHash: Props['urlHash'];
  setUrlHash: Dispatch<SetStateAction<Props['urlHash']>>;

  loading: Props['loading'];
  setLoading: Dispatch<SetStateAction<Props['loading']>>;

  quickShareDetails: Props['quickShareDetails'];
  setQuickShareDetails: Dispatch<SetStateAction<Props['quickShareDetails']>>;

  quickShareContent: Props['quickShareContent'];
  setQuickShareContent: Dispatch<SetStateAction<Props['quickShareContent']>>;

  submittedTotp: Props['submittedTotp'];
  setSubmittedTotp: Dispatch<SetStateAction<Props['submittedTotp']>>;

  totpSubmitting?: Props['totpSubmitting'];
  setTotpSubmitting: Dispatch<SetStateAction<Props['totpSubmitting']>>;

  isNotFound?: Props['isNotFound'];
  setIsNotFound: Dispatch<SetStateAction<Props['isNotFound']>>;

  isLoggingIn?: Props['isLoggingIn'];
  setIsLoggingIn: Dispatch<SetStateAction<Props['isLoggingIn']>>;

  refreshJwt?: Props['refreshJwt'];
  setRefreshJwt: Dispatch<SetStateAction<Props['refreshJwt']>>;

  quickSharePrivateKey?: Props['quickSharePrivateKey'];
  setQuickSharePrivateKey: Dispatch<SetStateAction<Props['quickSharePrivateKey']>>;

  loginError?: Props['loginError'];
  setLoginError: Dispatch<SetStateAction<Props['loginError']>>;

  realmLogo: Props['realmLogo'];
  setRealmLogo: Dispatch<SetStateAction<Props['realmLogo']>>;

  quickShareFilesStates: Props['quickShareFilesStates'];
  setQuickShareFilesStates: Dispatch<SetStateAction<Props['quickShareFilesStates']>>;

  quickShareFilesComments: Props['quickShareFilesComments'];
  setQuickShareFilesComments: Dispatch<SetStateAction<Props['quickShareFilesComments']>>;

  jwtGenerator: QuickShareApiJwtGenerator | undefined;
  quickShareKey: SymmetricKey | undefined;
  quickSharePublicKey: PublicKey | undefined;
};

const QuickShareContext = createContext<QuickShareContextType>({
  urlHash: undefined,
  setUrlHash: () => logger.warn('no QuickShare provider'),

  loading: undefined,
  setLoading: () => logger.warn('no QuickShare provider'),

  quickShareDetails: undefined,
  setQuickShareDetails: () => logger.warn('no QuickShare provider'),

  quickShareContent: undefined,
  setQuickShareContent: () => logger.warn('no QuickShare provider'),

  submittedTotp: undefined,
  setSubmittedTotp: () => logger.warn('no QuickShare provider'),

  totpSubmitting: undefined,
  setTotpSubmitting: () => logger.warn('no QuickShare provider'),

  isLoggingIn: undefined,
  setIsLoggingIn: () => logger.warn('no QuickShare provider'),

  isNotFound: undefined,
  setIsNotFound: () => logger.warn('no QuickShare provider'),

  refreshJwt: undefined,
  setRefreshJwt: () => logger.warn('no QuickShare provider'),

  quickSharePrivateKey: undefined,
  setQuickSharePrivateKey: () => logger.warn('no QuickShare provider'),

  loginError: undefined,
  setLoginError: () => logger.warn('no QuickShare provider'),

  realmLogo: undefined,
  setRealmLogo: () => logger.warn('no QuickShare provider'),

  quickShareFilesStates: undefined,
  setQuickShareFilesStates: () => logger.warn('no QuickShare provider'),

  quickShareFilesComments: undefined,
  setQuickShareFilesComments: () => logger.warn('no QuickShare provider'),

  jwtGenerator: undefined,
  quickShareKey: undefined,
  quickSharePublicKey: undefined,
});

export function useQuickShareContext(): QuickShareContextType {
  return useContext(QuickShareContext);
}

export function QuickShareProvider({ children }: Props): JSX.Element {
  const [urlHash, setUrlHash] = useState<Props['urlHash']>();
  const [loading, setLoading] = useState<Props['loading']>(true);
  const [quickShareDetails, setQuickShareDetails] = useState<Props['quickShareDetails']>();
  const [quickShareContent, setQuickShareContent] = useState<Props['quickShareContent']>();
  const [quickShareFilesStates, setQuickShareFilesStates] = useState<Props['quickShareFilesStates']>();
  const [quickShareFilesComments, setQuickShareFilesComments] = useState<Props['quickShareFilesComments']>();
  const [submittedTotp, setSubmittedTotp] = useState<Props['submittedTotp']>(); // the user submitted TOTP code
  const [totpSubmitting, setTotpSubmitting] = useState<Props['totpSubmitting']>(true);
  const [isLoggingIn, setIsLoggingIn] = useState<Props['isLoggingIn']>(false);
  const [isNotFound, setIsNotFound] = useState<Props['isNotFound']>(false);
  const [refreshJwt, setRefreshJwt] = useState<Props['refreshJwt']>();
  const [quickSharePrivateKey, setQuickSharePrivateKey] = useState<Props['quickSharePrivateKey']>();
  const [loginError, setLoginError] = useState<string>();
  const [realmLogo, setRealmLogo] = useState<string>();

  const quickSharePublicKey = useMemo((): PublicKey | undefined => {
    if (!quickShareDetails) {
      return undefined;
    }
    return PublicKey.fromBase64(quickShareDetails.publicKey);
  }, [quickShareDetails]);

  const quickShareKey = useMemo((): SymmetricKey | undefined => {
    if (!quickSharePrivateKey) {
      return undefined;
    }
    if (!quickShareContent) {
      return undefined;
    }
    const encryptedQuickShareKey = new Uint8Array(base64url.toBuffer(quickShareContent.encryptedQuickShareKey));
    const quickShareKey = quickSharePrivateKey.decrypt(encryptedQuickShareKey);
    return new SymmetricKey(quickShareKey);
  }, [quickShareContent, quickSharePrivateKey]);

  // A class that needs to be kept alive. It contains a pattern for generating
  // jwt tokens that are used to interact with the api.
  // FIXME: REACT 19 - disable for now. Should fix  Error: Compilation Skipped: Existing memoization could not be preserved
  // eslint-disable-next-line
  const jwtGenerator = useMemo((): QuickShareApiJwtGenerator | undefined => {
    if (!quickShareDetails) {
      return undefined;
    }
    if (!quickSharePrivateKey) {
      return undefined;
    }
    return new QuickShareApiJwtGenerator(quickSharePrivateKey, quickShareDetails.quickShareMemberId, refreshJwt);
  }, [quickSharePrivateKey, quickShareDetails]); // eslint-disable-line

  return (
    <QuickShareContext.Provider
      value={{
        urlHash,
        setUrlHash,
        loading,
        setLoading,
        quickShareDetails,
        setQuickShareDetails,
        quickShareContent,
        setQuickShareContent,
        submittedTotp,
        setSubmittedTotp,
        totpSubmitting,
        setTotpSubmitting,
        isLoggingIn,
        setIsLoggingIn,
        isNotFound,
        setIsNotFound,
        refreshJwt,
        setRefreshJwt,
        quickSharePrivateKey,
        setQuickSharePrivateKey,
        loginError,
        setLoginError,
        realmLogo,
        setRealmLogo,
        quickShareFilesStates,
        setQuickShareFilesStates,
        quickShareFilesComments,
        setQuickShareFilesComments,

        // computed values
        jwtGenerator,
        quickShareKey,
        quickSharePublicKey,
      }}
    >
      {children}
    </QuickShareContext.Provider>
  );
}
